<template>
	<div id="wrapper">
		<SidebarView :fixedAssets="true" :fixedAssetsVisible="true" :assets="true" />
		<div class="d-flex flex-column" id="content-wrapper">
			<div id="content">
				<TopbarView />
				<div class="mx-3">
					<div class="card card-shape home-box mb-3">
						<div class="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
							<h6 class="m-0 fw-bold text-green">Asset Register</h6>
							<b-button class="akkurate-green-btn btn-size text-white" @click="openCreate">
								<font-awesome-icon class="me-1" :icon="['fas', 'plus']" />
								Add Asset
							</b-button>
						</div>
						<div class="card-body">
							<div class="row g-2 mb-3 align-items-end">
								<div class="col-md-3">
									<label class="akkurate-dark small">Search</label>
									<b-form-input v-model="filters.q" class="field-container" placeholder="Name / tag..." @keyup.enter="loadList" />
								</div>
								<div class="col-md-3">
									<label class="akkurate-dark small">Branch</label>
									<multiselect v-model="filters.branch" :options="branches" label="name" track-by="id" placeholder="All" class="field-container" />
								</div>
								<div class="col-md-3">
									<label class="akkurate-dark small">Category</label>
									<multiselect v-model="filters.category" :options="categories" label="name" track-by="id" placeholder="All" class="field-container" />
								</div>
								<div class="col-md-3">
									<label class="akkurate-dark small">Status</label>
									<b-form-select v-model="filters.status" :options="statusOptions" class="field-container" />
								</div>
								<div class="col-12 d-flex justify-content-end gap-2">
									<b-button class="akkurate-green-btn btn-size text-white" @click="loadList">
										<font-awesome-icon class="me-1" :icon="['fas', 'search']" />
										Apply
									</b-button>
									<b-button variant="outline-secondary" class="btn-size" @click="resetFilters">
										Reset
									</b-button>
								</div>
							</div>

							<b-table
								striped
								bordered
								responsive
								:busy="busy"
								:items="rows"
								:fields="fields"
								show-empty
								class="table_text_size"
							>
								<template #table-busy>
									<div class="text-center text-danger my-2">
										<b-spinner class="align-middle" />
										<strong>Loading...</strong>
									</div>
								</template>
								<template #cell(actions)="d">
									<div class="d-flex flex-wrap gap-1">
										<b-button size="sm" variant="outline-info" @click="goView(d.item)">View</b-button>
										<b-button size="sm" variant="outline-secondary" @click="openEdit(d.item)">Edit</b-button>
									</div>
								</template>
							</b-table>
						</div>
					</div>
				</div>
			</div>
			<footer></footer>
		</div>
		<ChatBot />
		<a class="d-inline scroll-to-top" href="#page-top"><i class="fas fa-angle-up"></i></a>

		<b-modal v-model="showModal" size="xl" :title="editingId ? 'Edit asset' : 'Create asset'" @hidden="resetForm">
			<b-overlay :show="saving || loadingOne" rounded="sm">
				<div class="row g-2">
					<div class="col-md-6">
						<label class="akkurate-dark small">Asset name <span class="text-danger">*</span></label>
						<b-form-input v-model="form.asset_name" class="field-container" />
						<errors v-if="errors.asset_name">{{ errors.asset_name[0] }}</errors>
					</div>
					<div class="col-md-6">
						<label class="akkurate-dark small">Category <span class="text-danger">*</span></label>
						<multiselect v-model="form.category" :options="categories" label="name" track-by="id" class="field-container" />
						<errors v-if="errors.category_id">{{ errors.category_id[0] }}</errors>
					</div>
					<div class="col-md-6">
						<label class="akkurate-dark small">Branch <span class="text-danger">*</span></label>
						<multiselect v-model="form.branch" :options="branches" label="name" track-by="id" class="field-container" />
						<errors v-if="errors.branch_id">{{ errors.branch_id[0] }}</errors>
					</div>
					<div class="col-md-6">
						<label class="akkurate-dark small">Assigned to (optional)</label>
						<multiselect v-model="form.assigned_to" :options="users" label="full_name" track-by="id" class="field-container" placeholder="Unassigned" />
						<errors v-if="errors.assigned_to">{{ errors.assigned_to[0] }}</errors>
					</div>

					<div class="col-md-4">
						<label class="akkurate-dark small">Purchase date <span class="text-danger">*</span></label>
						<b-form-input v-model="form.purchase_date" type="date" class="field-container" />
						<errors v-if="errors.purchase_date">{{ errors.purchase_date[0] }}</errors>
					</div>
					<div class="col-md-4">
						<label class="akkurate-dark small">Purchase cost <span class="text-danger">*</span></label>
						<b-form-input v-model.number="form.purchase_cost" type="number" step="any" min="0" class="field-container" />
						<errors v-if="errors.purchase_cost">{{ errors.purchase_cost[0] }}</errors>
					</div>
					<div class="col-md-4">
						<label class="akkurate-dark small">Salvage value</label>
						<b-form-input v-model.number="form.salvage_value" type="number" step="any" min="0" class="field-container" />
						<errors v-if="errors.salvage_value">{{ errors.salvage_value[0] }}</errors>
					</div>

					<div class="col-md-4">
						<label class="akkurate-dark small">Depreciation method <span class="text-danger">*</span></label>
						<b-form-select v-model="form.depreciation_method" :options="deprMethodOptions" class="field-container" />
						<errors v-if="errors.depreciation_method">{{ errors.depreciation_method[0] }}</errors>
					</div>
					<div class="col-md-4">
						<label class="akkurate-dark small">Useful life (months) <span class="text-danger">*</span></label>
						<b-form-input v-model.number="form.useful_life_months" type="number" step="1" min="1" class="field-container" />
						<errors v-if="errors.useful_life_months">{{ errors.useful_life_months[0] }}</errors>
					</div>
					<div class="col-md-4">
						<label class="akkurate-dark small">Condition</label>
						<b-form-select v-model="form.condition_status" :options="conditionOptions" class="field-container" />
						<errors v-if="errors.condition_status">{{ errors.condition_status[0] }}</errors>
					</div>

					<div class="col-md-4">
						<label class="akkurate-dark small">Status</label>
						<b-form-select v-model="form.status" :options="statusOptions" class="field-container" />
						<errors v-if="errors.status">{{ errors.status[0] }}</errors>
					</div>

					<div v-if="formError" class="col-12">
						<div class="alert alert-danger py-2 small mb-0">{{ formError }}</div>
					</div>
				</div>
			</b-overlay>
			<template #modal-footer>
				<div class="w-100 d-flex justify-content-end">
					<b-button variant="secondary" class="me-2" @click="showModal = false">Cancel</b-button>
					<b-button class="akkurate-green-btn text-white" :disabled="saving || loadingOne" @click="save">
						<b-spinner v-if="saving" small class="me-1" />
						{{ editingId ? "Update" : "Create" }}
					</b-button>
				</div>
			</template>
		</b-modal>
	</div>
</template>

<script>
import SidebarView from "@/views/main/components/Sidebar.vue";
import TopbarView from "@/views/main/components/Topbar.vue";
import ChatBot from "@/views/components/AI/ChatBot.vue";
import ApiService from "@/core/services/api.service";
import Multiselect from "vue-multiselect";

export default {
	name: "AssetsIndex",
	components: { SidebarView, TopbarView, ChatBot, Multiselect },
	data() {
		return {
			busy: false,
			rows: [],
			fields: [
				{ key: "id", label: "#" },
				{ key: "asset_name", label: "Asset" },
				{ key: "status", label: "Status" },
				{ key: "branch", label: "Branch" },
				{ key: "category", label: "Category" },
				{ key: "assigned_to", label: "Assigned to" },
				{ key: "actions", label: "Actions" },
			],
			branches: [],
			categories: [],
			users: [],
			filters: {
				q: "",
				branch: null,
				category: null,
				status: "",
			},
			statusOptions: [
				{ value: "", text: "All" },
				{ value: "active", text: "Active" },
				{ value: "in_use", text: "In use" },
				{ value: "under_maintenance", text: "Under maintenance" },
				{ value: "disposed", text: "Disposed" },
			],

			showModal: false,
			editingId: null,
			loadingOne: false,
			saving: false,
			form: null,
			errors: {},
			formError: "",

			deprMethodOptions: [
				{ value: "straight_line", text: "Straight line" },
				{ value: "reducing_balance", text: "Reducing balance" },
			],
			conditionOptions: [
				{ value: "", text: "—" },
				{ value: "good", text: "Good" },
				{ value: "fair", text: "Fair" },
				{ value: "poor", text: "Poor" },
				{ value: "damaged", text: "Damaged" },
			],
		};
	},
	created() {
		this.form = this.blankForm();
		this.loadDropdowns();
		this.loadList();
	},
	methods: {
		blankForm() {
			return {
				asset_name: "",
				category: null,
				branch: null,
				assigned_to: null,
				purchase_date: "",
				purchase_cost: null,
				salvage_value: null,
				depreciation_method: "straight_line",
				useful_life_months: 12,
				status: "active",
				condition_status: "",
			};
		},
		async loadDropdowns() {
			try {
				const [br, cat, us] = await Promise.all([
					ApiService.get("/settings/branches/dropdown").catch(() => ({ data: {} })),
					ApiService.get("/asset-categories", { params: { per_page: 100, is_active: 1 } }).catch(() => ({ data: {} })),
					ApiService.get("/users/dropdown").catch(() => ({ data: {} })),
				]);
				this.branches = br.data?.data || br.data?.branches || br.data || [];
				this.categories = cat.data?.data || cat.data || [];
				this.users = us.data?.data || us.data || [];
			} catch (e) {
				/* non-blocking */
			}
		},
		resetFilters() {
			this.filters = { q: "", branch: null, category: null, status: "" };
			this.loadList();
		},
		async loadList() {
			this.busy = true;
			this.$Progress.start();
			try {
				const params = {
					per_page: 30,
					branch_id: this.filters.branch?.id || "",
					category_id: this.filters.category?.id || "",
					assigned_to: "",
					status: this.filters.status || "",
					q: this.filters.q || "",
				};
				const res = await ApiService.get("/assets", { params });
				const body = res.data;
				this.rows = body.data || body || [];
			} catch (e) {
				toast.fire({ icon: "error", title: e.response?.data?.message || "Failed to load assets" });
				this.rows = [];
			} finally {
				this.busy = false;
				this.$Progress.finish();
			}
		},
		goView(row) {
			this.$router.push({ path: `/assets/${row.id}` });
		},
		openCreate() {
			this.resetForm();
			this.showModal = true;
		},
		async openEdit(row) {
			this.resetForm();
			this.editingId = row.id;
			this.showModal = true;
			this.loadingOne = true;
			try {
				const res = await ApiService.get(`/assets/${row.id}`);
				const a = res.data?.data || res.data || {};
				this.form.asset_name = a.asset_name || "";
				this.form.category = this.categories.find((x) => x.id === a.category_id) || (a.category_id ? { id: a.category_id, name: `#${a.category_id}` } : null);
				this.form.branch = this.branches.find((x) => x.id === a.branch_id) || (a.branch_id ? { id: a.branch_id, name: `#${a.branch_id}` } : null);
				this.form.assigned_to = this.users.find((x) => x.id === a.assigned_to) || null;
				this.form.purchase_date = a.purchase_date || "";
				this.form.purchase_cost = a.purchase_cost;
				this.form.salvage_value = a.salvage_value;
				this.form.depreciation_method = a.depreciation_method || "straight_line";
				this.form.useful_life_months = a.useful_life_months || 12;
				this.form.status = a.status || "active";
				this.form.condition_status = a.condition_status || "";
			} catch (e) {
				this.formError = e.response?.data?.message || "Could not load asset.";
			} finally {
				this.loadingOne = false;
			}
		},
		resetForm() {
			this.editingId = null;
			this.loadingOne = false;
			this.saving = false;
			this.errors = {};
			this.formError = "";
			this.form = this.blankForm();
		},
		payload() {
			return {
				asset_name: this.form.asset_name,
				category_id: this.form.category?.id,
				branch_id: this.form.branch?.id,
				assigned_to: this.form.assigned_to?.id || null,
				purchase_date: this.form.purchase_date,
				purchase_cost: Number(this.form.purchase_cost || 0),
				salvage_value: this.form.salvage_value === null || this.form.salvage_value === "" ? null : Number(this.form.salvage_value),
				depreciation_method: this.form.depreciation_method,
				useful_life_months: Number(this.form.useful_life_months || 0),
				status: this.form.status,
				condition_status: this.form.condition_status || null,
			};
		},
		async save() {
			this.saving = true;
			this.errors = {};
			this.formError = "";
			this.$Progress.start();
			try {
				const payload = this.payload();
				if (this.editingId) {
					await ApiService.put(`/assets/${this.editingId}`, payload);
					toast.fire({ icon: "success", title: "Asset updated" });
				} else {
					await ApiService.post("/assets", payload);
					toast.fire({ icon: "success", title: "Asset created" });
				}
				this.showModal = false;
				this.loadList();
			} catch (e) {
				if (e.response?.status === 422 && e.response?.data?.errors) {
					this.errors = e.response.data.errors;
				} else {
					this.formError = e.response?.data?.message || "Save failed.";
				}
			} finally {
				this.saving = false;
				this.$Progress.finish();
			}
		},
	},
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

